import { axiosInstance } from "./config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { storage } from "../firebase/firebase-config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const projectsUrl = 'projects/';

// ==================== QUERIES ====================

export function useGetUserProjects() {
  return useQuery({
    queryKey: ['user-projects'],
    queryFn: async () => {
      const response = await axiosInstance.get(projectsUrl);
      return response.data;
    },
  });
}

export function useGetProjectById(projectId) {
  return useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await axiosInstance.get(`${projectsUrl}${projectId}/`);
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useGetProjectsBySkill(skillId, options = {}) {
  const { profileName = null, ...queryOptions } = options;
  
  return useQuery({
    queryKey: ['projects-by-skill', skillId, profileName],
    queryFn: async () => {
      const params = new URLSearchParams({ skill_id: skillId });
      if (profileName) params.append('profile_name', profileName);
      
      const response = await axiosInstance.get(
        `${projectsUrl}by_skill/?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!skillId,
    ...queryOptions,
  });
}

export function useGetPortfolioProjects(profileName) {
  return useQuery({
    queryKey: ['portfolio-projects', profileName],
    queryFn: async () => {
      const params = new URLSearchParams({ profile_name: profileName });
      const response = await axiosInstance.get(
        `${projectsUrl}portfolio/?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!profileName,
  });
}

// ==================== MUTATIONS ====================

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectData) => {
      const response = await axiosInstance.post(projectsUrl, projectData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, data }) => {
      const response = await axiosInstance.put(`${projectsUrl}${projectId}/`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', data.id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId) => {
      await axiosInstance.delete(`${projectsUrl}${projectId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

export function usePatchProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, data }) => {
      const response = await axiosInstance.patch(`${projectsUrl}${projectId}/`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      const key = String(variables.projectId);
      queryClient.setQueryData(['project', key], data);
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

export function useToggleProjectPublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, isPublished }) => {
      const response = await axiosInstance.patch(`${projectsUrl}${projectId}/`, {
        is_published: isPublished,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', data.id] });
    },
  });
}

export function useSetProjectCover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, screenshotUrl }) => {
      const response = await axiosInstance.patch(`${projectsUrl}${projectId}/`, {
        cover_image_url: screenshotUrl,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['project', String(variables.projectId)], data);
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

export function useUploadProjectCover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, file }) => {
      const formData = new FormData();
      formData.append('cover_image', file);

      const response = await axiosInstance.post(
        `${projectsUrl}${projectId}/upload_cover/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

export function useUploadProjectScreenshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, file }) => {
      const formData = new FormData();
      formData.append('screenshot', file);

      const response = await axiosInstance.post(
        `${projectsUrl}${projectId}/upload_screenshot/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      const key = String(variables.projectId);
      queryClient.setQueryData(['project', key], data);
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

export function useDeleteProjectScreenshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, screenshotUrl }) => {
      const response = await axiosInstance.delete(
        `${projectsUrl}${projectId}/delete_screenshot/`,
        { data: { screenshot_url: screenshotUrl } }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      const key = String(variables.projectId);
      queryClient.setQueryData(['project', key], data);
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
    },
  });
}

// ==================== FIREBASE IMAGE UTILITIES ====================

/**
 * Upload image to Firebase Storage directly from frontend
 * 
 * @param {File} file - File object from input
 * @param {string} folder - Folder path in Firebase Storage (default: 'projects')
 * @returns {Promise<string>} downloadURL - Public URL to the uploaded image
 */
export async function uploadImageToFirebase(file, folder = 'projects') {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(7);
    const fileName = `${timestamp}_${randomStr}_${file.name}`;
    
    // Create reference and upload
    const storageRef = ref(storage, `${folder}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image to Firebase:', error);
    throw error;
  }
}

/**
 * Upload multiple images to Firebase Storage
 * 
 * @param {File[]} files - Array of File objects
 * @param {string} folder - Folder path in Firebase Storage
 * @returns {Promise<string[]>} Array of download URLs
 */
export async function uploadMultipleImagesToFirebase(files, folder = 'projects') {
  try {
    const uploadPromises = files.map(file => uploadImageToFirebase(file, folder));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
}
